import { useAccountInfoQuery } from "@/hooks/api/useAccountQuery"
import { useVerifyQuery } from "@/hooks/api/useAuthQuery"
import { useTeamProfileInfoQuery } from "@/hooks/api/useTeamQuery"
import { useCountryListQuery } from "@/hooks/api/useUtilQuery"

import { createAccountStore } from "@/stores/account"
import { createGlobalStore } from "@/stores/global"
import { Country } from "@/types/account"
import { getTokenPayload } from "@/utils/token"

import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { convertCountryName, convertGroupAge } from "@/utils/format"
import { usePositionsQuery } from "@/hooks/api/usePositionQuery"
import { useNavigate } from "@tanstack/react-router"

export function useInitService(token: string) {
  const { selectedIdfTeam } = createAccountStore((state) => ({
    selectedIdfTeam: state.selectedIdfTeam,
  }))

  useVerifyQuery(token)
  useSetUser(token)
  useGetTeamProfileInfo(selectedIdfTeam)
  useGetCountryList()
  useGetPositionList()
}

export function useSetUser(token: string) {
  const { i18n } = useTranslation()
  const [accountId, setAccountId] = useState<number>()
  const navigate = useNavigate()
  const teamParamProcessedRef = useRef(false)

  const { setTeamInfo, setInfo, setSelectedIdfTeam } = createAccountStore((state) => ({
    setTeamInfo: state.setTeamInfo,
    setSelectedIdfTeam: state.setSelectedIdfTeam,
    setInfo: state.setInfo,
  }))
  const { data } = useAccountInfoQuery(accountId as number)

  const { idfAccount } = token.length > 0 ? getTokenPayload(token) : {}

  useEffect(() => {
    setAccountId(idfAccount)
  }, [idfAccount])

  useEffect(() => {
    if (data) {
      const {
        birthday,
        country,
        gender,
        idfAccount,
        image,
        name,
        type,
        teamDtoList,
        email,
        hasTableauRole,
        language,
      } = data || {}

      setInfo({
        birthday,
        country,
        gender,
        idfAccount,
        image,
        name,
        type,
        email,
        hasTableauRole,
      })
      setTeamInfo(teamDtoList)

      // 서버에 저장된 언어 설정이 있으면 적용 (source of truth: 서버)
      if (language) {
        void i18n.changeLanguage(language)
      }

      // --- 팀 선택 우선순위: URL ?team= > localStorage > 첫 번째 팀 ---
      const urlParams = new URLSearchParams(window.location.search)
      const teamParam = urlParams.get("team")

      if (teamParam && !teamParamProcessedRef.current) {
        // URL query param으로 팀 지정된 경우
        teamParamProcessedRef.current = true
        const urlTeamId = Number(teamParam)

        // URL에서 team param 제거 (다른 query param은 유지)
        const url = new URL(window.location.href)
        url.searchParams.delete("team")
        window.history.replaceState({}, "", url.pathname + url.search + url.hash)

        if (!isNaN(urlTeamId) && teamDtoList.some((t) => t.idfTeam === urlTeamId)) {
          // 유효한 팀: 해당 팀 선택
          setSelectedIdfTeam(urlTeamId)
        } else {
          // 무효한 팀: 첫 번째 팀 선택 후 /home으로 리다이렉트
          if (teamDtoList.length > 0) {
            setSelectedIdfTeam(teamDtoList[0].idfTeam)
          }
          void navigate({ to: "/home", replace: true })
        }
      } else if (!teamParam) {
        // URL param 없음: 기존 localStorage 로직
        const selectedIdfTeam = localStorage.getItem("selected_team")
        if (selectedIdfTeam) {
          const teamId = Number(JSON.parse(selectedIdfTeam).value)
          // 저장된 팀이 현재 팀 목록에 있는지 확인
          const isValidTeam = teamDtoList.some((team) => team.idfTeam === teamId)
          if (isValidTeam) {
            setSelectedIdfTeam(teamId)
          } else {
            // 저장된 팀이 유효하지 않으면 첫 번째 팀 선택
            if (teamDtoList.length > 0) {
              setSelectedIdfTeam(teamDtoList[0].idfTeam)
            }
          }
        } else {
          // localStorage에 팀이 없으면 첫 번째 팀을 자동 선택
          if (teamDtoList.length > 0) {
            setSelectedIdfTeam(teamDtoList[0].idfTeam)
          }
        }
      }
    }
  }, [data])
}

export function useGetTeamProfileInfo(idfTeams?: number) {
  const { setTeamProfileInfo } = createAccountStore((state) => ({
    setTeamProfileInfo: state.setTeamProfileInfo,
  }))
  const { data, isSuccess } = useTeamProfileInfoQuery(idfTeams as number)
  const { data: countryList } = useCountryListQuery()

  useEffect(() => {
    if (data) {
      const newCountry = countryList ? convertCountryName(data.country, countryList) : data.country

      setTeamProfileInfo({
        ...data,
        country: newCountry ?? data.country,
        groupAge: convertGroupAge(data.groupAge),
      })
    }
  }, [isSuccess])
}

export function useGetCountryList() {
  const { setCountryList } = createGlobalStore((state) => ({
    setCountryList: state.setCountryList,
  }))
  const { data, isSuccess } = useCountryListQuery()

  useEffect(() => {
    if (data) {
      setCountryList(data as Country[])
    }
  }, [isSuccess])
}

export function useGetPositionList() {
  const { setPositionList } = createGlobalStore((state) => ({
    setPositionList: state.setPositionList,
  }))
  const { data } = usePositionsQuery()

  useEffect(() => {
    if (data) {
      setPositionList(data)
    }
  }, [data])
}
